import type { FC, ReactNode } from "react";

import { VisaTag } from "@/components/common/VisaTag";
import { cn } from "@/lib/utils";
import { VISAVALUE, type NonEmptyVisa } from "@/types/visa.type";

import {
  SIGNAL_META,
  signalAsk,
  signalClock,
  signalFace,
  signalTone,
  TONE_CLASSES
} from "../signalTone";
import type { AttentionItem } from "../types";

const VISAS = new Set<string>(Object.values(VISAVALUE));

export const asVisa = (v: string | null | undefined): NonEmptyVisa | null =>
  v && v !== VISAVALUE.EMPTY && VISAS.has(v) ? (v as NonEmptyVisa) : null;

/** Line one: client name (truncates) and the case table's visa tag (never truncates). */
export const ClientLine: FC<{
  name: string;
  visa?: string | null;
  isRfe?: boolean;
  workflowType?: string | null;
  noticeLabel?: string | null;
  /** Shown as plain text when the visa isn't a known type. */
  fallbackTag?: string | null;
}> = ({ name, visa, isRfe, workflowType, noticeLabel, fallbackTag }) => {
  const tag = asVisa(visa);
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <span className="min-w-0 truncate text-base leading-4.5 font-semibold text-foreground">
        {name}
      </span>
      {tag ? (
        <span className="shrink-0">
          <VisaTag
            visaType={tag}
            isRfe={isRfe}
            workflowType={workflowType}
            noticeLabel={noticeLabel}
            size="xs"
          />
        </span>
      ) : fallbackTag ? (
        <span className="shrink-0 text-xs text-muted-foreground">
          {fallbackTag}
        </span>
      ) : null}
    </div>
  );
};

/** Line two: a kind label (or any short label) and a clock time. */
export const LabelLine: FC<{
  label: ReactNode;
  time: string;
  className?: string;
}> = ({ label, time, className }) => (
  <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
    <span className={cn("text-xs font-semibold tracking-wide", className)}>
      {label}
    </span>
    {time ? (
      <span className="text-xs text-muted-foreground">{time}</span>
    ) : null}
  </div>
);

export const itemName = (
  item: Pick<AttentionItem, "client_name" | "scope_kind" | "title">
): string =>
  item.client_name ||
  (item.scope_kind === "practice" ? "Practice" : item.title);

/**
 * The banner's three lines and nothing else: client and visa tag; the kind
 * label and local clock time with zone; what needs you (the problems when a
 * result has them, else the action).
 */
export const SignalBody: FC<{
  item: AttentionItem;
  clamp?: 1 | 2;
  now?: Date;
}> = ({ item, clamp = 2, now }) => {
  const face = signalFace(item);
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <ClientLine
        name={itemName(item)}
        visa={item.visa_type}
        isRfe={item.is_rfe}
        workflowType={item.workflow_type}
        noticeLabel={item.notice_label}
      />
      <LabelLine
        label={SIGNAL_META[face].label}
        time={signalClock(item.since, now)}
        className={TONE_CLASSES[signalTone(item)].text}
      />
      <p
        className={cn(
          "text-xs leading-4 text-fg-2",
          clamp === 1 ? "line-clamp-1" : "line-clamp-2"
        )}
      >
        {signalAsk(item)}
      </p>
    </div>
  );
};
